import type {
  LabsArchetype,
  LabsSwipeCard,
  VerticalPack,
  VerticalSlug,
} from '@/app/(platform)/(subscription-gated)/labs/_engine/types';

/**
 * Preview-only copy, layered over a pack without touching it.
 *
 * 🔴 WHY AN OVERRIDE AND NOT AN EDIT. Finance and Data are LIVE in Experiment 3 (enrolled 2026-08-24)
 * and their M1 and M2 screens read these exact strings. Editing the packs would change a running
 * experiment's wording mid-flight, which is both a measurement problem and an unreviewable one, since
 * nothing about the diff would say "this is live". Consulting is dark and would have been safe, but
 * fixing one vertical and not the others would leave the three previews reading differently.
 *
 * So the preview builds its own pack at mount: same content, a handful of strings replaced.
 *
 * THREE KINDS OF CHANGE HAPPEN HERE, and only the first is cosmetic:
 *
 *   1. Voice. Plainer sentences, fewer clauses, no em dashes. The reader is a student who typed an
 *      internship question into Google, not a subscriber halfway through a trial.
 *
 *   2. 🔴 THINGS THAT WERE UNTRUE IN A TWO-MODULE CUT. The sprint's copy counts to four and promises an
 *      artifact:
 *        - "One module down, three to go." There are two, and one is left.
 *        - "This will appear on your analysis at the end." There is no analysis; the preview drops M3
 *          and M4 entirely.
 *        - "That's the other half of your archetype." Product jargon, and the word never appears again
 *          on this surface.
 *      These are not polish. Each one tells a cold reader to expect something they will not get, which
 *      is the fastest way to lose them at the end rather than the start.
 *
 *   3. A PROMISE THE PRODUCT CANNOT KEEP. Every archetype body ends by naming tracks that "are your
 *      home", but the matched externships come from the pack's own curated list and need not contain
 *      them. On consulting this is a known open defect: THE OPERATOR is told people-analytics tracks
 *      are their home while the pack files People Analytics under DATA. The preview sidesteps it
 *      entirely by describing the PERSON and letting the cards underneath name the programs, which
 *      they do concretely and correctly.
 *
 * Anything not listed here falls through to the pack, so a fix to a swipe card or a slider question
 * lands on both surfaces at once. That is the point of layering rather than forking.
 */

/** Invariant across verticals: what the preview says at the two beats the sprint got wrong. */
const SHARED = {
  /** The bridge into M2. Sets the expectation honestly: it is short, and the result comes right after. */
  m1Bridge: {
    emphasis: 'think',
    post: ' when things get messy. Four quick questions, then your result.',
    pre: 'Next, a few questions about how you ',
  },
  /**
   * Under the M2 questions. The sprint points at an analysis the preview never produces, and an
   * earlier version of this line promised the result was on the NEXT screen, which stopped being true
   * the moment M3 was inserted after it.
   */
  m2ResultsFooter: 'This shapes the result you get at the end.',
} as const;

interface PreviewCopyOverride {
  /** Rewritten archetype bodies, keyed by archetype id. Taglines and names are left alone. */
  archetypeBodies: Readonly<Record<string, string>>;
  /** The M1 intro's framing paragraph, which is where the em dashes and the long clauses were. */
  m1IntroLead: string;
}

/**
 * Per-vertical rewrites. PARTIAL, because not every pack needs one.
 *
 * The three product verticals do: their copy was authored for a subscriber mid-trial and cannot be
 * edited in place while Experiment 3 reads those exact screens. The Career Explorer pack does not, and
 * must not have an entry here — it exists only on this surface, so its copy is written in preview voice
 * to begin with and a rewrite layer would be a second place to change the same sentence.
 */
const OVERRIDES: Partial<Readonly<Record<VerticalSlug, PreviewCopyOverride>>> = {
  consulting: {
    m1IntroLead:
      'There are two very different jobs hiding inside the word "consulting". Some people work out what a business should do next. Others make it actually happen, fixing the process that broke and the rollout that stalled. Most students never find out which one suits them until they are already doing it.',
    archetypeBodies: {
      gen: 'You can find the answer and then make it happen. That combination has a name: chief of staff. Very few people do both halves well, which is exactly why it is worth something.',
      operator:
        'You care about the work itself. The process that breaks as a company grows, the rollout that finally sticks, the fix that still holds after you leave the room.',
      strategist:
        'You care about the question behind the business. What should it do next, and what would make a room full of smart people actually believe it.',
    },
  },
  'data-analytics': {
    m1IntroLead:
      'There are two very different jobs hiding inside the word "data". Some people explain what happened and why, sitting with the team that has to act on it. Others build the systems those answers come out of. Most students never find out which one suits them until they are already doing it.',
    archetypeBodies: {
      builder:
        'You like structure. The query that becomes the source of truth, the schema that holds up, the result you can prove is not noise. You would rather an answer be right than be first.',
      gen: 'You can build the thing and then explain it to the person who has to act on it. That combination has a name: analytics engineer. Very few people do both halves well, which is exactly why it is worth something.',
      translator:
        'You care about the question behind the data. Who has to act on this, and what would actually change their mind. The numbers are the means, not the point.',
    },
  },
  finance: {
    m1IntroLead:
      'There are two very different jobs hiding inside the word "finance". Some people decide which companies are worth buying. Others run the numbers inside one company and help it decide what to do next. Most students never find out which one suits them until they are already doing it.',
    archetypeBodies: {
      corp: 'You like the questions that come from inside a business. What should we do, what is this worth, where is the money actually going. You would rather improve the thing than bet on it.',
      gen: 'You are comfortable on both sides, inside a business and outside it. That flexibility is rarer than it sounds, and it opens more doors early on.',
      invest:
        'You like the questions that come from outside a business. Is this price right, is this company worth what the market says it is. You are willing to make a call and defend it.',
    },
  },
};

/**
 * How many swipe cards the preview shows, down from the sprint's eight.
 *
 * Adding the M3 practice table bought the preview a real task and about three minutes. Trimming the
 * deck pays some of that back, and the deck is the cheapest place to find it: card seven tells a
 * reader nothing card three did not, whereas every step of M3 is new information. Net length lands
 * near seven minutes, the top of the range this surface was scoped to.
 */
const PREVIEW_DECK_SIZE = 6;

/**
 * Take the first N cards while keeping the two poles BALANCED.
 *
 * A naive `slice` would be a scoring bug, not a trim: `computeLean` reads the share of right-swipes
 * leaning to pole A, so a deck of five A-cards and one B-card would push almost every reader toward A
 * no matter what they picked. The archetype would then be an artefact of the trim.
 */
function balancedDeck(cards: readonly LabsSwipeCard[], size: number): readonly LabsSwipeCard[] {
  if (cards.length <= size) {
    return cards;
  }

  const perPole = Math.floor(size / 2);
  const kept = new Set<string>();
  for (const pole of ['a', 'b'] as const) {
    for (const card of cards.filter((c) => c.pole === pole).slice(0, perPole)) {
      kept.add(card.id);
    }
  }

  // Filtered from the ORIGINAL array so the deck keeps the pack's authored order rather than
  // surfacing all of one pole and then all of the other.
  return cards.filter((card) => kept.has(card.id));
}

/** Apply an archetype body override, leaving the name, tagline and accent exactly as the pack has them. */
function overrideArchetypes(
  archetypes: VerticalPack['archetypes'],
  bodies: Readonly<Record<string, string>>,
): VerticalPack['archetypes'] {
  const next: Record<string, LabsArchetype> = {};

  for (const [id, archetype] of Object.entries(archetypes)) {
    const body = bodies[id];
    next[id] = body ? { ...archetype, body } : archetype;
  }

  return next;
}

/**
 * The pack the preview renders: the real one, with the preview's copy layered on.
 *
 * Pure, and returns a NEW object, so it must be memoised by the caller. `PackProvider` resolves its
 * store by slug rather than by object identity, so a fresh pack object here cannot split the store.
 */
export function toPreviewPack(pack: VerticalPack): VerticalPack {
  const override = OVERRIDES[pack.slug];

  // 🔴 THE TRIM AND THE SHARED FIXES APPLY TO EVERY PACK, THE REWRITES ONLY TO PACKS THAT HAVE ONE.
  //
  // These used to be one branch: no override meant no trim and no shared copy either. That was correct
  // only for as long as every pack had an override. The Career Explorer pack does not, and under the
  // old shape it would have silently run an EIGHT-card deck on the surface whose whole constraint is
  // length, with the sprint's own bridge line pointing at modules it does not have.
  //
  // Deck size and the two corrected lines belong to the SURFACE, not to a vertical. Splitting them this
  // way is a no-op for finance, data and consulting, which all still take both halves.
  return {
    ...pack,
    archetypes: override ? overrideArchetypes(pack.archetypes, override.archetypeBodies) : pack.archetypes,
    m1: { ...pack.m1, cards: balancedDeck(pack.m1.cards, PREVIEW_DECK_SIZE) },
    copy: {
      ...pack.copy,
      m1: {
        ...pack.copy.m1,
        intro: override ? { ...pack.copy.m1.intro, lead: override.m1IntroLead } : pack.copy.m1.intro,
        results: {
          ...pack.copy.m1.results,
          bridge: { ...SHARED.m1Bridge },
        },
      },
      m2: {
        ...pack.copy.m2,
        results: { ...pack.copy.m2.results, footer: SHARED.m2ResultsFooter },
      },
    },
  };
}
